import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosPost from '../helpers/axios-helper';
import '../styles/Directory.css';
import { FaBars, FaCalendar, FaCarSide, FaChevronRight, FaGlobe, FaHospital, FaHotel, FaMapPin, FaShoppingCart, FaUmbrellaBeach, FaUniversity, FaUtensils, FaNewspaper, FaCompass } from 'react-icons/fa';
import { FaEarthAmericas, FaLocationPin } from 'react-icons/fa6';

export default function Directory() {
  const [options] = useState([
    { id: 'hotels', category: 'STAYS', icon: <FaHotel />, type: 'hotels' },
    { id: 'restaurants', category: 'RESTAURANTS', icon: <FaUtensils />, type: 'restaurants' },
    { id: 'experiences', category: 'EXPERIENCES', icon: <FaCompass />, type: 'experiences' },
    { id: 'events', category: 'EVENTS', icon: <FaCalendar />, type: 'events' },
    { id: 'articles', category: 'ARTICLES', icon: <FaNewspaper />, type: 'articles' }
  ]);

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchMode, setSearchMode] = useState(null); // 'country' or 'category'
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Fetch countries on mount
  useEffect(() => {
    const controller = new AbortController();
    axiosPost(
      'countries/list',
      {},
      {},
      (res) => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        setCountries(list);
      },
      controller
    );
    return () => controller.abort();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    const controller = new AbortController();
    
    if (selectedCountry || selectedCategory || searchText.trim()) {
      // Debounce search text input
      const timeoutId = setTimeout(() => {
        fetchResults(controller);
      }, searchText.trim() && !selectedCountry && !selectedCategory ? 500 : 0);
      
      return () => {
        clearTimeout(timeoutId);
        controller.abort();
      };
    } else {
      setResults([]);
      setLoading(false);
    }
    
    return () => controller.abort();
  }, [selectedCountry, selectedCategory, searchText]);

  const slugify = (str) => {
    try {
      return (str || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    } catch { return ''; }
  };

  const fetchResults = async (controller) => {
    setLoading(true);

    try {
      const payload = {};
      
      // Add filters to payload
      if (selectedCountry) {
        payload.country_id = selectedCountry.id;
      }
      
      if (selectedCategory) {
        payload.category = selectedCategory;
      }
      
      if (searchText.trim()) {
        payload.search = searchText.trim();
      }

      // Single API call to unified directory search
      axiosPost(
        'directory/search',
        {},
        payload,
        (res) => {
          const data = Array.isArray(res) ? res : (res?.data || []);
          setResults(data);
          setLoading(false);
        },
        controller
      );
    } catch (error) {
      console.error('Error fetching results:', error);
      setResults([]);
      setLoading(false);
    }
  };

  const handleCountrySearch = () => {
    setSearchMode('country');
    setShowCountryDropdown(!showCountryDropdown);
    setShowCategoryDropdown(false);
  };

  const handleCategorySearch = () => {
    setSearchMode('category');
    setShowCategoryDropdown(!showCategoryDropdown);
    setShowCountryDropdown(false);
  };

  const selectCountry = (country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
  };

  const selectCategory = (category) => {
    setSelectedCategory(category);
    setShowCategoryDropdown(false);
  };

  const clearFilters = () => {
    setSelectedCountry(null);
    setSelectedCategory(null);
    setSearchMode(null);
    setSearchText('');
    setResults([]);
  };

  const getItemLink = (item) => {
    const title = item.title || item.name || '';
    switch(item.category) {
      case 'hotels':
        return `/hotel/${item.id}/${slugify(title)}`;
      case 'restaurants':
        return `/restaurant/${item.id}/${slugify(title)}`;
      case 'experiences':
        return `/experience/${item.id}/${slugify(title)}`;
      case 'events':
        return `/event/${item.id}/${slugify(title)}`;
      case 'articles':
        return `/article/${item.slug || item.id}`;
      default:
        return '#';
    }
  };

  const getItemImage = (item) => {
    if (item.image) return item.image;
    if (item.featured_image_url) return item.featured_image_url;
    if (item.image_url) return item.image_url;
    if (item.images) {
      const imgs = item.images.split(/[,\n]/).filter(i => i.trim());
      if (imgs.length > 0) return imgs[0].trim();
    }
    return process.env.PUBLIC_URL + '/assets/placeholder.jpg';
  };

  const getItemTitle = (item) => {
    return item.title || item.name || 'Untitled';
  };

  const getItemLocation = (item) => {
    const countryName = countries.find(c => c.id === item.country_id)?.name || '';
    // Prioritize address over location (location often contains coordinates)
    const address = item.address || '';
    return address ? `${address}, ${countryName}` : countryName;
  };
  
  const renderOptions = () => {
    return options.map((option, index) => {
      const isLast = index === options.length - 1;
      const isSelected = selectedCategory === option.type;

      return (
        <div 
          key={option.id}
          className={`option ${isSelected ? 'option-selected' : ''}`}
          style={{borderRight: (isLast) ? '' : '2px solid #2c2c2c'}}
          onClick={() => selectCategory(option.type)}
        >
          <div className='option-icon'>
            {option.icon}
          </div>
          <div className='option-category'>{option.category}</div>
        </div>
      );
    });
  };

  const renderResults = () => {
    if (loading) {
      return (
        <div className='directory-loading'>
          <div className='loading-spinner'></div>
          <p>Loading results...</p>
        </div>
      );
    }

    if (results.length === 0 && (selectedCountry || selectedCategory || searchText)) {
      return (
        <div className='directory-no-results'>
          <p>No results found for your search criteria.</p>
          <button onClick={clearFilters} className='clear-filters-btn'>Clear Filters</button>
        </div>
      );
    }

    return results.map((item, index) => {
      const itemLink = getItemLink(item);
      const itemImage = getItemImage(item);
      const itemTitle = getItemTitle(item);
      const itemLocation = getItemLocation(item);

      const getCategoryIcon = () => {
        switch(item.category) {
          case 'hotels': return <FaHotel />;
          case 'restaurants': return <FaUtensils />;
          case 'experiences': return <FaCompass />;
          case 'events': return <FaCalendar />;
          case 'articles': return <FaNewspaper />;
          default: return null;
        }
      };

      return (
        <Link to={itemLink} key={`${item.category}-${item.id}-${index}`} className='destination-item'>
          <div 
            className='destination-image'
            style={{backgroundImage: `url(${itemImage})`, backgroundSize: 'cover', backgroundPosition: 'center'}}
          >
            <div className='destinations-item-category'>
              {getCategoryIcon()}
              <span>{item.categoryLabel}</span>
            </div>
          </div>
          <div className='destination-info'>
            <div className='destination-title'>{itemTitle}</div>
            {itemLocation && (
              <div style={{display: 'flex', alignItems: 'center', marginTop: '10px'}}>
                <div>
                  <FaMapPin size={12}/>
                </div>
                <div className='destination-subtitle'>{itemLocation.toUpperCase()}</div>
              </div>
            )}
            {item.phone && (
              <div className='destination-subtitle' style={{border: 'none', margin: 0, marginTop: '5px'}}>
                {item.phone}
              </div>
            )}
          </div>
        </Link>
      );
    });
  };

  return (
    <div style={{paddingTop:'90px'}}>
      <div className='directory-landing' style={{backgroundImage: `url(${process.env.PUBLIC_URL + '/assets/directory_landing.jpg'})`}}>
        <div className='directory-landing-overlay'></div>
        <div className='directory-landing-content'>
          <div className='directory-landing-title'>Find your next <br/>Destination</div>
          <div className='directory-landing-subtitle'>Discover exciting places, top-rated hotels, and hidden gems. Start exploring by entering a destination or keyword to plan your perfect trip!</div>
          
          {/* Search Input */}
          <div className='directory-search-input-wrapper'>
            <input 
              type='text'
              className='directory-search-input'
              placeholder='Search by name or location...'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <button 
                className='directory-search-clear'
                onClick={() => setSearchText('')}
              >
                ×
              </button>
            )}
          </div>

          <div className='directory-landing-options'>
            <div 
              className={`directory-landing-option ${selectedCountry ? 'option-active' : ''}`}
              onClick={handleCountrySearch}
            >
              {selectedCountry ? selectedCountry.name : 'Search by Country'}
              <FaEarthAmericas size={18} color='#D8D8D8'/>
            </div>
            <div 
              className={`directory-landing-option ${selectedCategory ? 'option-active' : ''}`}
              onClick={handleCategorySearch}
            >
              {selectedCategory ? selectedCategory.toUpperCase() : 'Search by Category'}
              <FaBars size={18} color='#D8D8D8'/>
            </div>
          </div>

          {/* Country Dropdown */}
          {showCountryDropdown && (
            <div className='directory-dropdown'>
              <div className='directory-dropdown-header'>
                <span>Select a Country</span>
                <button onClick={() => setShowCountryDropdown(false)}>×</button>
              </div>
              <div className='directory-dropdown-list'>
                {countries.map(country => (
                  <div 
                    key={country.id}
                    className='directory-dropdown-item'
                    onClick={() => selectCountry(country)}
                  >
                    {country.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Category Dropdown */}
          {showCategoryDropdown && (
            <div className='directory-dropdown'>
              <div className='directory-dropdown-header'>
                <span>Select a Category</span>
                <button onClick={() => setShowCategoryDropdown(false)}>×</button>
              </div>
              <div className='directory-dropdown-list'>
                {options.map(option => (
                  <div 
                    key={option.id}
                    className='directory-dropdown-item'
                    onClick={() => selectCategory(option.type)}
                  >
                    <span className='dropdown-icon'>{option.icon}</span>
                    {option.category}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Filters */}
          {(selectedCountry || selectedCategory) && (
            <div className='directory-active-filters'>
              <span>Active Filters:</span>
              {selectedCountry && (
                <div className='filter-tag'>
                  {selectedCountry.name}
                  <button onClick={() => setSelectedCountry(null)}>×</button>
                </div>
              )}
              {selectedCategory && (
                <div className='filter-tag'>
                  {selectedCategory.toUpperCase()}
                  <button onClick={() => setSelectedCategory(null)}>×</button>
                </div>
              )}
              <button onClick={clearFilters} className='clear-all-btn'>Clear All</button>
            </div>
          )}
        </div>
      </div>

      <div className='directory-categories'>
        <div className='directory-categories-wrapper'>
          {renderOptions()}
        </div>
      </div>

      <div className='section-title' style={{width: '100%', textAlign: 'center', marginTop: '50px'}}>
        {(selectedCountry || selectedCategory || searchText) 
          ? `Search Results ${!loading && results.length > 0 ? `(${results.length})` : ''}` 
          : 'Explore All Categories'}
      </div>
      <div className='directory-destinations-wrapper'>
        {renderResults()}
      </div>

      <div className='directory-ad-space'>
        <div className='directory-ad-sub'>
          <img src={process.env.PUBLIC_URL + '/assets/email.png'} className='directory-email-icon'/>
          <div className='directory-email-text'>
            Let Us Stay<br/>
            In Touch With You.
          </div>

          <div className='directory-email-input-container'>
            <div className='directory-email-input-wrapper'>
              <input type='text' className='directory-email-input' placeholder='Enter your mail'/>

              <div className='directory-email-input-icon'>
                <FaChevronRight size={20} color='#fff'/>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
